/**
 * Gift Cards API
 *
 * API functions for studio gift card operations.
 */

import { api } from "./api-client";

// ============================================================================
// Types
// ============================================================================

export interface GiftCardSettings {
  id?: string;
  studio_id?: string;
  enabled: boolean;
  min_amount: number | null; // cents, null = no minimum
  max_amount: number | null; // cents, null = no maximum
  customer_fee_ratio: number;
}

export interface GiftCard {
  id: string;
  studio_id: string;
  code: string;
  amount: number; // cents
  purchaser_email: string | null;
  purchaser_name: string | null;
  recipient_name: string;
  recipient_email: string;
  note: string | null;
  status: "pending_payment" | "active" | "redeemed";
  stripe_payment_intent_id: string | null;
  redeemed_by: string | null;
  redeemed_at: string | null;
  credit_transaction_id: string | null;
  created_at: string;
}

export interface PurchaseGiftCardRequest {
  studio_id: string;
  amount: number; // cents
  recipient_name: string;
  recipient_email: string;
  purchaser_name?: string;
  purchaser_email?: string;
  note?: string;
}

export interface PurchaseGiftCardResponse {
  gift_card_id: string;
  client_secret: string;
}

export interface ValidateGiftCardResponse {
  code_type: "gift_card";
  gift_card_id: string;
  amount: number;
  studio_name: string;
}

export interface RedeemGiftCardResponse {
  studio_id: string;
  studio_name: string;
  amount: number;
  new_balance: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get gift card settings for a studio
 */
export async function getGiftCardSettings(
  studioId: string
): Promise<GiftCardSettings> {
  return api.get<GiftCardSettings>(
    `/gift-cards/studios/${studioId}/settings`
  );
}

/**
 * Update gift card settings for a studio
 */
export async function updateGiftCardSettings(
  studioId: string,
  settings: Partial<GiftCardSettings>
): Promise<GiftCardSettings> {
  return api.put<GiftCardSettings>(
    `/gift-cards/studios/${studioId}/settings`,
    settings
  );
}

/**
 * Purchase a gift card
 */
export async function purchaseGiftCard(
  data: PurchaseGiftCardRequest
): Promise<PurchaseGiftCardResponse> {
  return api.post<PurchaseGiftCardResponse>("/gift-cards/purchase", data);
}

/**
 * Confirm a gift card purchase after payment
 */
export async function confirmGiftCardPurchase(
  giftCardId: string
): Promise<GiftCard> {
  return api.post<GiftCard>(
    `/gift-cards/${giftCardId}/confirm`,
    {}
  );
}

/**
 * Validate a gift card code
 */
export async function validateGiftCardCode(
  code: string,
  studioId: string
): Promise<ValidateGiftCardResponse> {
  return api.post<ValidateGiftCardResponse>("/gift-cards/validate", {
    code,
    studio_id: studioId,
  });
}

/**
 * Redeem a gift card
 */
export async function redeemGiftCard(
  code: string
): Promise<RedeemGiftCardResponse> {
  return api.post<RedeemGiftCardResponse>("/gift-cards/redeem", { code });
}

/**
 * Get all gift cards for a studio
 */
export async function getStudioGiftCards(
  studioId: string
): Promise<{ gift_cards: GiftCard[]; total: number }> {
  return api.get(`/gift-cards/studios/${studioId}`);
}
